import { API_URL, isMocksEnabled } from "./helpers";
import {
  getPersonInfoMock,
  getRecurConfigPageMock,
  getRecurInstancePageMock,
} from "../__mocks__/PersonInfo";
import {
  Account,
  ParseResumeFromS3Args,
  PersonInfoApi,
  PersonRegionApi,
  RecurArgs,
  RecurCancelArgs,
  RecurConfigEx,
  RecurCreate,
  RecurEditArgs,
  RecurFilterArgs,
  RecurFilterOption,
  RecurInstanceArgs,
  RecurInstancePage,
  RecurRestartArgs,
  RecurVerifyArgs,
  SubscriptionRecurInstancePage,
} from "./Person";
import { useAppContext } from "../AppProviders";
import { useMutation, useQuery } from "react-query";
import { Resume } from "./Resume";
import { KeyValue } from "../reports/utils";
import { XPage } from "./GenericModels";

function filterEnrichedPersonInfo(personInfo: PersonInfoApi) {
  return {
    birth_date: personInfo.birth_date,
    names: personInfo.names.filter((name) => !name.enriched_by_item),
    jobs: personInfo.jobs.filter((name) => !name.enriched_by_item),
    addresses: personInfo.addresses.filter((name) => !name.enriched_by_item),
    degrees: personInfo.degrees.filter((name) => !name.enriched_by_item),
    phones: personInfo.phones.filter((name) => !name.enriched_by_item),
    emails: personInfo.emails.filter((name) => !name.enriched_by_item),
    identifications: personInfo.identifications.filter(
      (name) => !name.enriched_by_item
    ),
  };
}
export function useGetPersonInfo() {
  const { requestApi } = useAppContext();

  const getPersonInfo = async (person_uuid: string): Promise<PersonInfoApi> => {
    if (isMocksEnabled()) {
      return await getPersonInfoMock("94488a8ebb4b4784b83294368079d338");
    }
    return requestApi
      .get(`${API_URL}/person_info/${person_uuid}`)
      .then((response) => {
        return filterEnrichedPersonInfo(response.data);
      });
  };

  const mutationResult = useMutation(
    (input: string) => getPersonInfo(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

/** A person's region never changes after creation, so one fetch per person is enough. */
export function usePersonRegion(
  personUuid: string | undefined,
  enabled: boolean = true
) {
  const { requestApi } = useAppContext();
  const getPersonRegion = async (): Promise<PersonRegionApi> => {
    if (isMocksEnabled()) {
      return { person_uuid: personUuid ?? "", region: "US" };
    }
    const response = await requestApi.get(
      `${API_URL}/person/${personUuid}/region`
    );
    return response.data;
  };
  return useQuery(["personRegion", personUuid], getPersonRegion, {
    enabled: enabled && !!personUuid && !!requestApi,
    staleTime: Infinity,
    retry: false,
  });
}

/** Payload for 360 person edit (name, email, phone, birth_date). Send null for birth_date to clear it. */
export interface PersonInfo360EditPayload {
  birth_date?: string | null;
  names?: Array<{
    first_name: string;
    middle_name?: string;
    last_name: string;
    name_type: string;
  }>;
  emails?: Array<{ address: string }>;
  phones?: Array<{ raw_number: string }>;
  report_uuid: string;
}

export interface PersonInfoAddressesPayload {
  addresses: Array<{
    external_id?: string | null;
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    start_date?: string;
    end_date?: string;
  }>;
  report_uuid?: string;
}

export interface PersonInfoJobsPayload {
  jobs: Array<{
    external_id?: string | null;
    title: string;
    employer: string;
    country: string;
    start_date: string;
    end_date: string;
  }>;
  report_uuid?: string;
}

export interface PersonInfoDegreesPayload {
  degrees: Array<{
    external_id?: string | null;
    name: string;
    educator: string;
    country: string;
    start_date: string;
    end_date: string;
  }>;
  report_uuid?: string;
}

export function useUpdatePerson360Info() {
  const { requestApi } = useAppContext();

  const updatePerson360Info = async (
    personUuid: string,
    payload: PersonInfo360EditPayload
  ): Promise<void> => {
    if (isMocksEnabled()) {
      return;
    }
    await requestApi.post(
      `${API_URL}/360/person_info/${personUuid}/edit`,
      payload
    );
  };

  const mutationResult = useMutation(
    ({
      personUuid,
      payload,
    }: {
      personUuid: string;
      payload: PersonInfo360EditPayload;
    }) => updatePerson360Info(personUuid, payload),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdatePersonAddresses() {
  const { requestApi } = useAppContext();

  const updatePersonAddresses = async (
    personUuid: string,
    payload: PersonInfoAddressesPayload,
    source?: string
  ): Promise<void> => {
    if (isMocksEnabled()) {
      return;
    }
    const url = `${API_URL}/person_info/${personUuid}/addresses`;
    const urlWithSource = source
      ? `${url}?source=${encodeURIComponent(source)}`
      : url;
    await requestApi.put(urlWithSource, payload);
  };

  const mutationResult = useMutation(
    ({
      personUuid,
      payload,
      source,
    }: {
      personUuid: string;
      payload: PersonInfoAddressesPayload;
      source?: string;
    }) => updatePersonAddresses(personUuid, payload, source),
    {}
  );

  return { ...mutationResult };
}

export function useUpdatePersonJobs() {
  const { requestApi } = useAppContext();

  const updatePersonJobs = async (
    personUuid: string,
    payload: PersonInfoJobsPayload,
    source?: string
  ): Promise<void> => {
    if (isMocksEnabled()) {
      return;
    }
    const url = `${API_URL}/person_info/${personUuid}/jobs`;
    const urlWithSource = source
      ? `${url}?source=${encodeURIComponent(source)}`
      : url;
    await requestApi.put(urlWithSource, payload);
  };

  const mutationResult = useMutation(
    ({
      personUuid,
      payload,
      source,
    }: {
      personUuid: string;
      payload: PersonInfoJobsPayload;
      source?: string;
    }) => updatePersonJobs(personUuid, payload, source),
    {}
  );

  return { ...mutationResult };
}

export function useUpdatePersonDegrees() {
  const { requestApi } = useAppContext();

  const updatePersonDegrees = async (
    personUuid: string,
    payload: PersonInfoDegreesPayload,
    source?: string
  ): Promise<void> => {
    if (isMocksEnabled()) {
      return;
    }
    const url = `${API_URL}/person_info/${personUuid}/degrees`;
    const urlWithSource = source
      ? `${url}?source=${encodeURIComponent(source)}`
      : url;
    await requestApi.put(urlWithSource, payload);
  };

  const mutationResult = useMutation(
    ({
      personUuid,
      payload,
      source,
    }: {
      personUuid: string;
      payload: PersonInfoDegreesPayload;
      source?: string;
    }) => updatePersonDegrees(personUuid, payload, source),
    {}
  );

  return { ...mutationResult };
}

/**
 * Person-domain accounts for a subject (`GET persondal/accounts/person_id/{uuid}`).
 *
 * 360 workbench reads these instead of social-profile Items on purpose: accounts are
 * a static list on the person (unknown/OTHER sites included). Item rows can be
 * soft-deleted, taken out of report, or amended, so they are not a stable 360
 * identity list. Report Summary / Profile Confirm / scrape still use Items.content.url.
 */
export function useGetPersonAccounts() {
  const { requestApi } = useAppContext();
  const getPersonAccounts = async (person_uuid: string): Promise<Account[]> => {
    return requestApi
      .get(`${API_URL}/persondal/accounts/person_id/${person_uuid}`)
      .then((response) => {
        return response.data;
      });
  };
  const mutationResult = useMutation(
    (input: string) => getPersonAccounts(input),
    {}
  );
  return { ...mutationResult };
}

export function useCreatePersonEmpty() {
  const { requestApi } = useAppContext();
  const createPersonEmpty = async (
    companyUuid: string | null
  ): Promise<string> => {
    return requestApi
      .post(`${API_URL}/person/create/empty`, undefined, {
        params: companyUuid ? { company_uuid: companyUuid } : undefined,
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.error("Error creating person empty", error);
      });
  };
  const mutationResult = useMutation(
    (companyUuid: string | null) => createPersonEmpty(companyUuid),
    {}
  );
  return { ...mutationResult };
}

export function useUploadResume() {
  const { requestApi } = useAppContext();

  const uploadResume = async (resume: File): Promise<Resume> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (event) => {
        if (event.target && event.target.result) {
          const base64String = (event.target.result as string).split(",")[1];
          const payload = {
            fileName: resume.name,
            content: base64String,
          };

          try {
            const response = await requestApi.post(
              `${API_URL}/resume/`,
              payload
            );
            resolve(response.data);
          } catch (error) {
            reject(error);
          }
        }
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(resume);
    });
  };

  const mutationResult = useMutation((input: File) => uploadResume(input), {});

  return {
    ...mutationResult,
  };
}

export function useParseResumeFromS3() {
  const { requestApi } = useAppContext();

  const parseResumeFromS3 = async (
    bucket: string,
    key: string,
    id: string
  ): Promise<Resume> => {
    return requestApi
      .post(`${API_URL}/resume/s3`, { bucket: bucket, key: key, id: id })
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (args: ParseResumeFromS3Args) =>
      parseResumeFromS3(args.bucket, args.key, args.id),
    {}
  );
  return { ...mutationResult };
}

export function useCreateRecur() {
  const { requestApi } = useAppContext();

  const createRecur = async (input: RecurCreate): Promise<string> => {
    const params = {
      company_uuid: input.company_uuid,
      report_uuid: input.report_uuid,
      blueprint_uuid: input.blueprint_uuid,
      behavior_kit: input.behavior_kit,
      start_date: input.start_date,
      schedule_value: input.schedule_value,
      custom_id: input.custom_id,
      run_now: input.run_now,
    };
    return requestApi
      .post(`${API_URL}/person/${input.person_uuid}/recur`, params)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: RecurCreate) => createRecur(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetRecurList() {
  const { requestApi } = useAppContext();
  const getRecurList = async (
    input: RecurArgs
  ): Promise<XPage<RecurConfigEx>> => {
    if (isMocksEnabled()) {
      return await getRecurConfigPageMock();
    }
    const { individual, ...query } = input;
    const params = new URLSearchParams(
      JSON.parse(JSON.stringify(query))
    ).toString();
    const endpoint = individual ? "/recur/individual" : "/recur";
    return requestApi
      .get(`${API_URL}${endpoint}?${params}`)
      .then((response) => {
        return response.data;
      });
  };
  const mutationResult = useMutation(
    (input: RecurArgs) => getRecurList(input),
    {}
  );
  return { ...mutationResult };
}

export function useGetRecurFilter() {
  const { requestApi } = useAppContext();
  const getRecurFilter = async (
    input: RecurFilterArgs
  ): Promise<RecurFilterOption[]> => {
    const { individual, ...query } = input;
    const params = new URLSearchParams(
      JSON.parse(JSON.stringify(query))
    ).toString();
    const endpoint = individual ? "/recur/filter/individual" : "/recur/filter";
    return requestApi
      .get(`${API_URL}${endpoint}?${params}`)
      .then((response) => {
        return response.data;
      });
  };
  const mutationResult = useMutation(
    (input: RecurFilterArgs) => getRecurFilter(input),
    {}
  );
  return { ...mutationResult };
}

export function useCancelRecur() {
  const { requestApi } = useAppContext();
  const cancelRecur = async (input: RecurCancelArgs): Promise<KeyValue[]> => {
    return requestApi
      .post(`${API_URL}/recur/cancel`, input)
      .then((response) => {
        return response.data;
      });
  };
  const mutationResult = useMutation(
    (input: RecurCancelArgs) => cancelRecur(input),
    {}
  );
  return { ...mutationResult };
}

export function useRestartRecur() {
  const { requestApi } = useAppContext();
  const restartRecur = async (input: RecurRestartArgs): Promise<KeyValue[]> => {
    return requestApi
      .post(`${API_URL}/recur/restart`, input)
      .then((response) => {
        return response.data;
      });
  };
  const mutationResult = useMutation(
    (input: RecurRestartArgs) => restartRecur(input),
    {}
  );
  return { ...mutationResult };
}

export function useRecurVerify() {
  const { requestApi } = useAppContext();
  const recurVerify = async (input: RecurVerifyArgs): Promise<void> => {
    return requestApi
      .post(`${API_URL}/recur/verify`, input)
      .then((response) => {
        return response.data;
      });
  };
  const mutationResult = useMutation(
    (input: RecurVerifyArgs) => recurVerify(input),
    {}
  );
  return { ...mutationResult };
}

export function useRecurEdit() {
  const { requestApi } = useAppContext();
  const recurEdit = async (input: RecurEditArgs): Promise<void> => {
    return requestApi.post(`${API_URL}/recur/edit`, input).then((response) => {
      return response.data;
    });
  };
  const mutationResult = useMutation(
    (input: RecurEditArgs) => recurEdit(input),
    {}
  );
  return { ...mutationResult };
}

export function useGetRecurInstances() {
  const { requestApi } = useAppContext();
  const getRecurInstances = async (
    input: RecurInstanceArgs
  ): Promise<RecurInstancePage> => {
    if (isMocksEnabled()) {
      return await getRecurInstancePageMock();
    }
    return requestApi
      .get(`${API_URL}/recur/instances`, input)
      .then((response) => {
        return response.data;
      });
  };
  const mutationResult = useMutation(
    (input: RecurInstanceArgs) => getRecurInstances(input),
    {}
  );
  return { ...mutationResult };
}

/** Subscription management page: recur instances with ProductReports status. */
export function useGetSubscriptionRecurInstances() {
  const { requestApi } = useAppContext();
  const getSubscriptionRecurInstances = async (
    input: RecurInstanceArgs
  ): Promise<SubscriptionRecurInstancePage> => {
    if (isMocksEnabled()) {
      const page = await getRecurInstancePageMock();
      return {
        ...page,
        data: page.data.map((row) => ({
          ...row,
          product_report_status: row.request_uuid ? "DONE" : null,
        })),
      };
    }
    return requestApi
      .get(`${API_URL}/recur/subscription_instances`, input)
      .then((response) => {
        return response.data;
      });
  };
  const mutationResult = useMutation(
    (input: RecurInstanceArgs) => getSubscriptionRecurInstances(input),
    {}
  );
  return { ...mutationResult };
}
